import { LinearProgress } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import toast from "react-hot-toast";
import JSZip from "jszip";
import { styled } from "@mui/material/styles";
import axios from "axios";
import DragNdrop from "./DragNdrop";
import { useTranslation } from "react-i18next";
import AppButton from "../../../shared/AppButton";

export default function UploadClaimAttachmentModal({
  showUploadAttachmentModal,
  setShowUploadAttachmentModal,
  currentUploadData,
  setCurrentUploadData,
}) {
  const [loadingFile, setLoadingFile] = useState(false);
  const { t } = useTranslation();

  const getCurrentDate = () => {
    const currentDate = new Date();
    const day = String(currentDate.getDate()).padStart(2, "0");
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const year = String(currentDate.getFullYear());
    return `${year}-${month}-${day}`;
  };

  const onImageChange = (file) => {
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        formik.setFieldValue("attachment", {
          content_type: file.type,
          data: reader.result?.split(",")[1],
          filename: file.name,
          creation_date: getCurrentDate(),
        });
      };
    }
  };

  const formik = useFormik({
    initialValues: { file: null, data: [] },

    validationSchema: () =>
      Yup.object().shape({
        file: Yup.mixed()
          .required(t("Attachment required"))
          .test(
            "fileFormat",
            t("Unsupported file format. Only ZIP files are allowed."),
            (value) => {
              if (value instanceof File) {
                return (
                  value.type === "application/zip" ||
                  value.type === "application/x-zip-compressed"
                );
              }
              return false;
            },
          )
          .test(
            "fileSize",
            t("File size is too large. Maximum size is 1GB."),
            (value) => {
              if (value instanceof File) {
                return value.size <= 1 * 1024 * 1024 * 1024;
              }
              return false;
            },
          ),
        data: Yup.array()
          .of(
            Yup.object().shape({
              claim_no: Yup.string().required(),
              attachment: Yup.object()
                .shape({
                  content_type: Yup.string().required(),
                  data: Yup.string().required(),
                  filename: Yup.string().required(),
                  creation_date: Yup.date().required(),
                })
                .required(),
            }),
          )
          .test(
            "dataValidation",
            t(
              "All data entries must have claim_no, content_type, data, filename, and creation_date.",
            ),
            (value) => {
              if (!Array.isArray(value)) return false;
              for (const item of value) {
                if (
                  !item.claim_no ||
                  !item.attachment ||
                  !item.attachment.content_type ||
                  !item.attachment.data ||
                  !item.attachment.filename ||
                  !item.attachment.creation_date
                ) {
                  return false;
                }
              }
              return true;
            },
          )
          .required(t("Data is required.")),
      }),

    onSubmit: async (values) => {
      delete values?.file;
      const token = localStorage.getItem("token");
      const tokenWithoutQuotes = token.replace(/"/g, "");
      if (!token) {
        console.error(t("Token not found in local storage."));
        return;
      }
      try {
        setLoadingFile(true);
        const response = await axios.post(
          `https://api.supergitsa.com/api/claim/excel/attachment?excel_id=${currentUploadData?.ReferenceNo}`,
          values?.data,
          { headers: { Authorization: `Bearer ${tokenWithoutQuotes}` } },
        );
        setLoadingFile(false);
        toast.success(response.data?.message);
        formik.resetForm();
        setShowUploadAttachmentModal(false);
        setCurrentUploadData(null);
      } catch (error) {
        setLoadingFile(false);
        console.log(error);
        toast.error(error?.response?.data?.error);
      }
    },
  });

  const [formattedData, setFormattedData] = useState([]);

  const handleFileUpload = async () => {
    const file = formik.values.file;
    if (file && file.name.toLowerCase().endsWith(".zip")) {
      await handleZipFile(file);
    } else {
      alert(t("Please upload a ZIP file."));
    }
  };

  const handleZipFile = async (file) => {
    setLoadingFile(true);
    const zip = new JSZip();
    const zipContent = await file.arrayBuffer();
    const unzipped = await zip.loadAsync(zipContent);
    const entries = [];
    const currentDate = new Date().toISOString().split("T")[0];

    await Promise.all(
      Object.entries(unzipped.files).map(async ([relativePath, zipEntry]) => {
        if (!zipEntry.dir) {
          const fileContent = await zipEntry.async("base64");
          const fileName = zipEntry.name;
          const baseFileName = fileName
            .replace(/\s*\(\d+\)\.\w+$/, "")
            .split(".")[0];
          const fileExtension = fileName.split(".").pop();
          const contentType = `application/${fileExtension}`;
          entries.push({
            claim_no: baseFileName,
            attachment: {
              content_type: contentType,
              data: fileContent,
              filename: fileName,
              creation_date: currentDate,
            },
          });
        }
      }),
    );

    setLoadingFile(false);
    setFormattedData(entries);
    formik.setFieldValue("data", entries);
  };

  useEffect(() => {
    if (formik.values.file) {
      handleFileUpload();
    }
  }, [formik.values.file]);

  return (
    <Transition.Root show={showUploadAttachmentModal} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[9999]"
        onClose={() => setShowUploadAttachmentModal(false)}
      >
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                  <Dialog.Title className="text-sm font-bold text-[#1e293b] uppercase tracking-wide">
                    {t("Upload Attachment")}
                  </Dialog.Title>
                  <button
                    onClick={() => setShowUploadAttachmentModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-opacity"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Progress Bar */}
                {loadingFile && (
                  <LinearProgress
                    sx={{
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#0070C2",
                      },
                    }}
                  />
                )}

                {/* Body */}
                <div className="p-3 space-y-3 max-h-[70vh] overflow-y-auto">
                  {/* Instructions */}
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <h2 className="text-xs font-bold text-[#0070C2] uppercase tracking-wide mb-2">
                      {t("Instructions")}
                    </h2>
                    <ul className="list-decimal pl-4 space-y-1 text-[11px] text-gray-600">
                      <li>
                        <span className="font-semibold">{t("File Type")}:</span>{" "}
                        {t("Only ZIP files are allowed.")}
                      </li>
                      <li>
                        <span className="font-semibold">
                          {t("Minimum Size")}:
                        </span>{" "}
                        {t(
                          "The File NamingZIP file must be at least 10MB in size.",
                        )}
                      </li>
                      <li>
                        <span className="font-semibold">
                          {t("File Organization")}:
                        </span>{" "}
                        {t("All files within the")}{" "}
                        {t(
                          "ZIP should not be placed inside any folders; they should",
                        )}{" "}
                        {t("be at the root level.")}
                      </li>
                      <li>
                        <span className="font-semibold">
                          {t("File Naming")}:
                        </span>{" "}
                        {t(
                          "If there are two or more files for the same claim number, use the following naming convention:",
                        )}
                        <ul className="list-disc pl-4 mt-1 space-y-1">
                          <li>
                            {t("For the first file")}:{" "}
                            <code className="bg-gray-100 px-1 rounded">
                              {t("claimnumber.pdf")}
                            </code>{" "}
                            ({t("e.g.")},{" "}
                            <code className="bg-gray-100 px-1 rounded">
                              {t("243344.pdf")}
                            </code>
                            )
                          </li>
                          <li>
                            {t("For the second file")}:{" "}
                            <code className="bg-gray-100 px-1 rounded">
                              {t("claimnumber (1).pdf")}
                            </code>{" "}
                            ({t("e.g.")},{" "}
                            <code className="bg-gray-100 px-1 rounded">
                              {t("243344 (1).pdf")}
                            </code>
                            )
                          </li>
                          <li>
                            {t("For the third file")}:{" "}
                            <code className="bg-gray-100 px-1 rounded">
                              {t("claimnumber (2).pdf")}
                            </code>{" "}
                            ({t("e.g.")},{" "}
                            <code className="bg-gray-100 px-1 rounded">
                              {t("243344 (2).pdf")}
                            </code>
                            )
                          </li>
                        </ul>
                      </li>
                    </ul>
                  </div>

                  {/* DragNdrop */}
                  <DragNdrop
                    formik={formik}
                    width="100%"
                    height="100%"
                    formattedData={formattedData}
                    setFormattedData={setFormattedData}
                  />

                  {formik.errors.file && formik.touched.file && (
                    <p className="text-xs text-red-500">{formik.errors.file}</p>
                  )}
                  {formik.errors?.data && formik.touched?.data && (
                    <p className="text-xs text-red-500">
                      {formik.errors?.data}
                    </p>
                  )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 px-3 pb-3 border-t border-gray-100 bg-gray-50">
                  <AppButton
                    label={t("Cancel")}
                    variant="outlined"
                    onClick={() => setShowUploadAttachmentModal(false)}
                    className="!text-gray-600 !border-gray-300 hover:!bg-gray-100"
                  />
                  <AppButton
                    label={loadingFile ? "..." : t("Upload")}
                    disabled={loadingFile}
                    onClick={formik.handleSubmit}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
